import { Observable } from 'rxjs';

import type { ApiObservablePromise } from '@/types';
import type { ApiSuccessResponse } from '@nf/types/api';
import type { ReservationCreationPayload, ReservationCreationResult } from '@/types/booked-bet';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { Effect } from '@nf/utils-common/fpw/monadic';
import { fetchAndGetJwtHeader } from '@/utils/web';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { idealRoundFixed } from '@nf/utils-common/numbers';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { calculatePayOut, getDisplayOdds } from '@/utils/odds-utils';

const fixedDecimalBetTypes = [5, 413];

const formatBookingValue = (
	bookingValue: number,
	oddsType: number,
	priceType: number,
	betType: number
) => {
	return fixedDecimalBetTypes.includes(betType)
		? getDisplayOdds(bookingValue, oddsType, priceType, betType)
		: idealRoundFixed(bookingValue, 2);
};

const calculatePayoutRange = ({
	oddsRange, 
	ticketStake,
	bookingOddsType,
	ticketPriceType,
}: {
	oddsRange: Array<number>; 
	ticketStake: number;
	bookingOddsType: number;
	ticketPriceType: number;
}): Array<number> => {
	if (oddsRange.length !== 2) return [0, 0];

	const payoutRange = 
		oddsRange
			.map(
				value => calculatePayOut(
					value,
					ticketStake,
					bookingOddsType,
					ticketPriceType
				)
			)
			.sort((a, b) => a - b);

	return payoutRange;
};

const createReservation = (payload: ReservationCreationPayload): Observable<
	ApiObservablePromise<ApiSuccessResponse<ReservationCreationResult>>
> => {
	const authToken = getAuthToken();
	const { apiDomain } = useSiteStore.getState();

	return Effect(fetchComposer.postWithBearer(
		cloudUrlParser(`${apiDomain.ctcdDomain}/api/Reservation/CreateReservationTicket`),
		{
			body: JSON.stringify(payload)
		}
	))
		.map(fetchAndGetJwtHeader)
		.run(authToken);
};

export const reservationService = {
	fixedDecimalBetTypes,
	formatBookingValue,
	calculatePayoutRange,
	createReservation
};